import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-scroll';
import { RiGithubFill, RiLinkedinFill, RiArrowDownLine, RiMapPin2Line } from 'react-icons/ri';
import { HiArrowUpRight, HiArrowDownTray } from 'react-icons/hi2';
import { fadeUp, stagger } from '../../utils/animations';
import PROFILE from '../../images/ujjal-zaman-nyc.jpg';
const ROLES = ['Full-Stack Developer', 'React Specialist', 'API Architect', 'UI Engineer'];

export default function HeroSection() {
  const [roleIdx,   setRoleIdx]   = useState(0);
  const [mousePos,  setMousePos]  = useState({ x: 50, y: 50 });
  const [imgError,  setImgError]  = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx(i => (i + 1) % ROLES.length), 3000);
    return () => clearInterval(t);
  }, []);

  const handleMouseMove = useCallback((e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width)  * 100,
      y: ((e.clientY - rect.top)  / rect.height) * 100,
    });
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex flex-col overflow-hidden bg-ink-950"
    >
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="bg-blob bg-blob-1" />
      <div className="bg-blob bg-blob-2" />
      {/* Cursor spotlight */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-700"
        style={{
          background: `radial-gradient(800px circle at ${mousePos.x}% ${mousePos.y}%, rgba(99,102,241,0.08), transparent 60%)`,
        }}
      />
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-ink-950 to-transparent pointer-events-none" />

      {/* ── Content ── */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center justify-center lg:justify-between
                      gap-14 xl:gap-24 max-w-7xl mx-auto w-full px-6 sm:px-8 pt-28 pb-10">

        {/* ── LEFT ── */}
        <motion.div
          variants={stagger(0.08)}
          initial="hidden"
          animate="show"
          className="flex-1 flex flex-col text-center lg:text-left max-w-2xl"
        >
          {/* Location + status pill */}
          <motion.div variants={fadeUp} className="flex items-center justify-center lg:justify-start gap-2 mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 glass rounded-full border border-white/[0.07]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              <span className="font-mono text-[11px] text-emerald-400 font-semibold">Available</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 glass rounded-full border border-white/[0.07]">
              <RiMapPin2Line className="w-3 h-3 text-zinc-500" />
              <span className="font-mono text-[11px] text-zinc-500">New York, USA · Onsite & Remote</span>
            </div>
          </motion.div>

          {/* Role rotation */}
          <motion.div variants={fadeUp} className="mb-3 h-5 flex items-center justify-center lg:justify-start gap-2">
            <span className="font-mono text-[10px] text-zinc-700 uppercase tracking-[0.2em]">I build as a</span>
            <div className="h-3.5 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span
                  key={roleIdx}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{    opacity: 0, y: -14 }}
                  transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                  className="font-mono text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em] block"
                >
                  {ROLES[roleIdx]}
                </motion.span>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeUp}
            className="font-display font-extrabold text-white leading-[0.88] tracking-tight"
            style={{ fontSize: 'clamp(4.5rem, 13vw, 9.5rem)' }}
          >
            Ujjal<br />
            <span style={{
              background: 'linear-gradient(130deg, #a5b4fc 0%, #c084fc 45%, #67e8f9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Zaman.
            </span>
          </motion.h1>

          {/* Bio */}
          <motion.p
            variants={fadeUp}
            className="mt-6 text-base sm:text-lg text-zinc-500 leading-relaxed max-w-lg mx-auto lg:mx-0"
          >
            I craft <span className="text-zinc-300 font-medium">fast, beautiful</span> web &amp; mobile
            applications — front-end to backend — with 4+ years of commercial experience.
          </motion.p>

          {/* Quick stats row */}
          <motion.div
            variants={fadeUp}
            className="mt-6 flex items-center justify-center lg:justify-start gap-6"
          >
            {[
              { value: '4+',  label: 'Years exp' },
              { value: '50+', label: 'Projects'  },
              { value: '2',   label: 'Countries' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center lg:text-left">
                <p className="font-display font-extrabold text-xl text-white">{value}</p>
                <p className="font-mono text-[10px] text-zinc-700 uppercase tracking-widest">{label}</p>
              </div>
            ))}
            <div className="h-8 w-px bg-white/[0.06]" />
            <div className="flex items-center gap-2">
              {[
                { href: 'https://github.com/Ujjalzaman',            Icon: RiGithubFill,   label: 'GitHub'   },
                { href: 'https://www.linkedin.com/in/ujjal-zaman',  Icon: RiLinkedinFill, label: 'LinkedIn' },
              ].map(({ href, Icon, label }) => (
                <a
                  key={label} href={href} target="_blank" rel="noopener noreferrer"
                  aria-label={label}
                  className="p-2 rounded-xl glass border border-white/[0.06] text-zinc-600 hover:text-white hover:border-white/20 transition-all duration-200"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3"
          >
            <Link
              to="projects" smooth duration={700} offset={-64}
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl font-semibold text-sm text-white
                bg-indigo-600 hover:bg-indigo-500 transition-all duration-200 shadow-glow-sm hover:shadow-glow-md cursor-pointer"
            >
              View my work
              <HiArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <a
              href="https://drive.google.com/file/d/1GEYvUD7g0UEldrNOwuPBe8oGIH16bJir/view"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl font-medium text-sm text-zinc-400
                border border-white/[0.09] hover:border-white/20 hover:text-white hover:bg-white/[0.03]
                transition-all duration-200"
            >
              <HiArrowDownTray className="w-4 h-4" />
              Resume
            </a>
          </motion.div>
        </motion.div>

        {/* ── RIGHT: Developer card ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center gap-4 flex-shrink-0"
        >
          <div className="relative">
            <div
              className="absolute inset-[-24px] rounded-[44px] blur-3xl opacity-25 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #6366f1 0%, #8b5cf6 60%, transparent 100%)' }}
            />

            <div className="relative w-[200px] min-[400px]:w-[220px] sm:w-[240px] xl:w-[270px]">
              <div className="absolute inset-[-2px] rounded-[34px] overflow-hidden">
                <div
                  className="spin-conic absolute"
                  style={{
                    inset: '-100%',
                    background: 'conic-gradient(from 0deg at 50% 50%, #6366f1 0%, #a78bfa 20%, #22d3ee 45%, transparent 55%, #8b5cf6 70%, #6366f1 100%)',
                  }}
                />
              </div>

              {/* Card */}
              <div className="relative rounded-[32px] overflow-hidden bg-ink-900">
                <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  {imgError ? (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-ink-800 to-ink-700">
                      <span className="font-display font-extrabold text-6xl text-indigo-500/30">UZ</span>
                    </div>
                  ) : (
                    <img
                      src={PROFILE}
                      alt="Ujjal Zaman"
                      onError={() => setImgError(true)}
                      className="w-full h-full object-cover"
                      fetchPriority="high"
                    />
                  )}
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(to top, rgba(10,13,22,0.95) 0%, rgba(10,13,22,0.4) 40%, transparent 65%)' }}
                  />
                </div>

                {/* Card footer */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                    <span className="font-mono text-[10px] text-emerald-400">Open to work</span>
                  </div>
                  <p className="font-display font-extrabold text-xl text-white leading-tight">Ujjal Zaman</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="font-mono text-[11px] text-indigo-400">Full-Stack Developer</span>
                    <span className="text-zinc-700">·</span>
                    <span className="font-mono text-[10px] text-zinc-600">New York</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Two floating badges below the card */}
          <div className="flex gap-3 w-full max-w-[280px] sm:max-w-none justify-center">
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="flex-1 flex items-center gap-2.5 px-3.5 py-3 glass border border-white/[0.08] rounded-2xl"
            >
              <span className="text-xl flex-shrink-0">⚡</span>
              <div>
                <p className="text-xs font-bold text-white leading-none">4+ Years</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">Commercial exp.</p>
              </div>
            </motion.div>
            <motion.div
              animate={{ y: [0, -7, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="flex-1 flex items-center gap-2.5 px-3.5 py-3 glass border border-white/[0.08] rounded-2xl"
            >
              <span className="text-xl flex-shrink-0">🚀</span>
              <div>
                <p className="text-xs font-bold text-white leading-none">50+ Projects</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">Shipped</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* ── Auto-scrolling tech marquee ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.7 }}
        className="relative z-10 py-4 overflow-hidden border-t border-white/[0.04] bg-white/[0.01] flex-shrink-0"
      >
        <div className="flex overflow-hidden select-none" aria-hidden="true">
          <div className="marquee-inner">
            {[...Array(4)].map((_, rep) => (
              <span key={rep} className="flex items-center gap-0 whitespace-nowrap">
                {['React', 'Next.js', 'TypeScript', 'Angular', 'Node.js', 'Express', 'MongoDB',
                  'Prisma', 'AWS', 'Docker', 'React Native', 'Tailwind CSS', 'Redux', 'Firebase',
                  'PostgreSQL', 'GraphQL', 'Jest', 'CI/CD'].map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-2 mx-5 text-[11px] font-semibold text-zinc-800 uppercase tracking-widest hover:text-indigo-400 transition-colors cursor-default">
                    <span className="w-1 h-1 rounded-full bg-indigo-600/40 flex-shrink-0" />
                    {t}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-1 text-zinc-800 hover:text-indigo-400 transition-colors cursor-pointer"
      >
        <Link to="about" smooth duration={700} offset={-64}>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
            <RiArrowDownLine className="w-5 h-5" />
          </motion.div>
        </Link>
      </motion.div>
    </section>
  );
}
